import { useEffect, type ReactNode } from "react";
import { useInView } from "@/hooks/use-in-view";

export function Reveal({
  children,
  as: Tag = "div",
  delay = 0,
  className = "",
}: {
  children: ReactNode;
  as?: "div" | "section" | "article" | "li";
  delay?: number;
  className?: string;
}) {
  const { ref, inView } = useInView<HTMLElement>();

  // Reveal ist eine progressive Verbesserung: Im SSR-HTML bleibt der Inhalt
  // sichtbar. Erst nach erfolgreicher Hydrierung wird die Startposition für
  // die Animation aktiviert. Ein Sicherheits-Timer verhindert, dass Inhalte
  // bei einem ausbleibenden Observer dauerhaft verborgen bleiben.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      el.classList.add("is-visible");
      return;
    }
    el.classList.add("reveal-animate");
    const fallback = window.setTimeout(() => el.classList.add("is-visible"), 1_500);
    return () => window.clearTimeout(fallback);
  }, [ref]);

  // Honour the original delay prop so staggered reveals still cascade.
  useEffect(() => {
    if (!inView || !ref.current) return;
    const el = ref.current;

    // Double rAF ensures the browser paints the initial (hidden) state
    // at least once before the transition kicks in — avoids a first-frame
    // jump when the element is already inside the viewport on mount.
    let raf1 = 0;
    let raf2 = 0;
    let t = 0;
    const activate = () => {
      raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => el.classList.add("is-visible"));
      });
    };

    if (delay <= 0) {
      activate();
    } else {
      t = window.setTimeout(activate, delay);
    }

    // Release the compositor layer once the transition ends.
    const clearWillChange = () => {
      el.style.willChange = "auto";
    };
    el.addEventListener("transitionend", clearWillChange, { once: true });

    return () => {
      if (t) window.clearTimeout(t);
      if (raf1) cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      el.removeEventListener("transitionend", clearWillChange);
    };
  }, [inView, delay, ref]);

  return (
    <Tag ref={ref as never} className={`reveal ${className}`}>
      {children}
    </Tag>
  );
}
